import { useState, useEffect, useCallback } from 'react'
import { MessageCircle, RefreshCw, Send, CheckCircle2, XCircle, Loader2, PowerOff } from 'lucide-react'
import api from '../../services/api'

const STATUS_LABELS = {
  conectado: { label: 'Conectado', color: 'text-green-700 bg-green-50 border-green-200', icon: CheckCircle2 },
  conectando: { label: 'Conectando…', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: Loader2 },
  desconectado: { label: 'Desconectado', color: 'text-gray-600 bg-gray-50 border-gray-200', icon: XCircle },
}

export default function WhatsAppConfig() {
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [conectando, setConectando] = useState(false)
  const [desconectando, setDesconectando] = useState(false)
  const [qrcode, setQrcode] = useState(null)
  const [erro, setErro] = useState('')
  const [sucesso, setSucesso] = useState('')

  const [uazapiUrl, setUazapiUrl] = useState('')
  const [uazapiToken, setUazapiToken] = useState('')

  const [telefoneTeste, setTelefoneTeste] = useState('')
  const [enviandoTeste, setEnviandoTeste] = useState(false)

  const fetchConfig = useCallback(() => {
    api.get('/auth/super-admin/whatsapp')
      .then(r => {
        setConfig(r.data)
        setUazapiUrl(r.data.uazapi_url || '')
      })
      .catch(() => setErro('Erro ao carregar configuração.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { fetchConfig() }, [fetchConfig])

  // Enquanto estiver "conectando", faz polling do status a cada 3s
  useEffect(() => {
    if (config?.status !== 'conectando') return
    const interval = setInterval(async () => {
      try {
        const r = await api.get('/auth/super-admin/whatsapp/status')
        setConfig(r.data)
        if (r.data.status === 'conectado') {
          setQrcode(null)
          setSucesso('WhatsApp conectado com sucesso!')
        }
      } catch { /* mantém tentando */ }
    }, 3000)
    return () => clearInterval(interval)
  }, [config?.status])

  const handleSalvar = async (e) => {
    e.preventDefault()
    setErro('')
    setSucesso('')
    setSalvando(true)
    try {
      const r = await api.put('/auth/super-admin/whatsapp', {
        uazapi_url: uazapiUrl.trim(),
        uazapi_token: uazapiToken.trim(),
      })
      setConfig(r.data)
      setUazapiToken('')
      setSucesso('Configuração salva.')
    } catch (err) {
      setErro(err.response?.data?.detail || 'Erro ao salvar configuração.')
    } finally {
      setSalvando(false)
    }
  }

  const handleConectar = async () => {
    setErro('')
    setSucesso('')
    setConectando(true)
    setQrcode(null)
    try {
      const r = await api.post('/auth/super-admin/whatsapp/connect')
      setQrcode(r.data.qrcode)
      setConfig(c => ({ ...c, status: r.data.status }))
    } catch (err) {
      setErro(err.response?.data?.detail || 'Erro ao iniciar conexão.')
    } finally {
      setConectando(false)
    }
  }

  const handleDesconectar = async () => {
    if (!window.confirm('Isso vai desconectar o WhatsApp atual (' + (config?.numero_conectado || '') + '). Só será possível usar de novo depois de escanear um novo QR Code. Confirma?')) return
    setErro('')
    setSucesso('')
    setDesconectando(true)
    try {
      const r = await api.post('/auth/super-admin/whatsapp/disconnect')
      setConfig(r.data)
      setQrcode(null)
      setSucesso('WhatsApp desconectado.')
    } catch (err) {
      setErro(err.response?.data?.detail || 'Erro ao desconectar.')
    } finally {
      setDesconectando(false)
    }
  }

  const handleEnviarTeste = async (e) => {
    e.preventDefault()
    setErro('')
    setSucesso('')
    setEnviandoTeste(true)
    try {
      await api.post('/auth/super-admin/whatsapp/test', { telefone: telefoneTeste.trim() })
      setSucesso('Mensagem de teste enviada — confira o WhatsApp informado.')
    } catch (err) {
      setErro(err.response?.data?.detail || 'Erro ao enviar mensagem de teste.')
    } finally {
      setEnviandoTeste(false)
    }
  }

  if (loading) {
    return <div className="text-gray-500 text-sm">Carregando...</div>
  }

  const statusInfo = STATUS_LABELS[config?.status] || STATUS_LABELS.desconectado
  const StatusIcon = statusInfo.icon

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <MessageCircle size={24} /> WhatsApp — Verificação em duas etapas
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Instância Uazapi usada para enviar o código de verificação (2FA) por WhatsApp.
          Enquanto não conectada, o código continua sendo enviado só por e-mail.
        </p>
      </div>

      {erro && (
        <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg text-sm text-red-700">{erro}</div>
      )}
      {sucesso && (
        <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded-r-lg text-sm text-green-700">{sucesso}</div>
      )}

      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium ${statusInfo.color}`}>
        <StatusIcon size={14} className={config?.status === 'conectando' ? 'animate-spin' : ''} />
        {statusInfo.label}
        {config?.numero_conectado && <span className="text-gray-500 font-normal">— {config.numero_conectado}</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <form onSubmit={handleSalvar} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4 h-fit">
          <h2 className="text-sm font-semibold text-gray-700">Dados da instância</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL da instância</label>
            <input
              type="text"
              value={uazapiUrl}
              onChange={e => setUazapiUrl(e.target.value)}
              placeholder="https://sua-instancia.uazapi.com"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Token</label>
            <input
              type="password"
              value={uazapiToken}
              onChange={e => setUazapiToken(e.target.value)}
              placeholder={config?.uazapi_token_mascarado || 'Token da instância'}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            {config?.uazapi_token_mascarado && (
              <p className="text-xs text-gray-400 mt-1">Atual: {config.uazapi_token_mascarado}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={salvando}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </form>

        <div className="space-y-4 sm:space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700">Conexão</h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleConectar}
                disabled={conectando || !config?.uazapi_url}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                {conectando ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                {config?.status === 'conectado' ? 'Reconectar (novo QR Code)' : 'Conectar'}
              </button>

              {config?.status === 'conectado' && (
                <button
                  onClick={handleDesconectar}
                  disabled={desconectando}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {desconectando ? <Loader2 size={16} className="animate-spin" /> : <PowerOff size={16} />}
                  Desconectar
                </button>
              )}
            </div>

            {qrcode && (
              <div className="text-center pt-2">
                <img src={qrcode} alt="QR Code WhatsApp" className="mx-auto max-w-[240px] w-full border rounded-lg" />
                <p className="text-xs text-gray-500 mt-2">
                  Abra o WhatsApp no celular → Aparelhos conectados → escaneie o código acima.
                </p>
              </div>
            )}
          </div>

          {config?.status === 'conectado' && (
            <form onSubmit={handleEnviarTeste} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <h2 className="text-sm font-semibold text-gray-700">Testar envio</h2>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={telefoneTeste}
                  onChange={e => setTelefoneTeste(e.target.value)}
                  placeholder="5534999999999"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="submit"
                  disabled={enviandoTeste}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50"
                >
                  <Send size={14} /> {enviandoTeste ? 'Enviando...' : 'Enviar teste'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
